import React, {useState, useRef, useEffect} from 'react';
import {
  StyledDropdown,
  DropdownInput,
  StyledLabel,
  StyledLabelText,
  StyledIcon,
  StyledDropdownBoxWrapper,
  StyledDropdownBox,
  StyledDropdownBoxOption,
} from './styles';

import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {useTranslations} from '../../hooks/use-translations';
import {
  ChevronDown,
  TextAppearance,
  TextColor,
  TextCustom,
  TextCustomProps,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';
import disabled from '../icons/disabled';
import {labelTextStyle} from './helpers';
import SortArrow from '../icons/sort-arrow';

interface SearchResultsSortProps {
  onHandleSelectedSort: (value?: string) => void;
  sortByValue: string;
}

const DEFAULT_DROPDOWN_OPTIONS = [
  {value: 'distance', label: 'Distance'},
  {value: 'adjustedListPrice', label: 'Price'},
  {value: 'odometer', label: 'Mileage'},
  {value: 'model', label: 'Model'},
];

/**
 * Aux get current option object
 * @param options
 * @param selectedValue
 * @returns
 */
const getSelectedOption = (options: any[], selectedValue) =>
  options.find((option) => option.value === selectedValue);

export const SearchResultsSortV2: React.FC<SearchResultsSortProps> = (
  props,
) => {
  const {onHandleSelectedSort, sortByValue} = props;
  const [showOption, setShowOption] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [labelValue, setLabelValue] = useState<{label: string, value: string} | undefined>(undefined);
  const {sortFilterOptions = DEFAULT_DROPDOWN_OPTIONS} = useFeatureAppConfig();
  const {sortLabel, sortShortLabel} =
    useTranslations().resultsSection.headerResults;

  const dropdownTextStyle: TextCustomProps = {
    appearance: TextAppearance.headline200,
    tag: TextTag.span,
    style: {
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight24,
      fontWeight: TextWeight.regular,
      color: TextColor.primary,
    },
  };
  /** Change dropdown status */
  const toggleShowOption = () => {
    setShowOption(!showOption);
    // onTouch && showOption && onTouch();
  };

  /**
   * Handle dropdown change event
   * @param value
   */
  const handleDropdownChange = (value: string) => {
    const option = getSelectedOption(sortFilterOptions, value);
    setShowOption(false);
    if(option) {
      setLabelValue(option);
      value && onHandleSelectedSort(option.value);
    }
  };

  /**
   * Hide dropdown option box when user is interacting with another component
   * @param event
   */
  const handleMouseDown = (event: any) => {
    !dropdownRef?.current?.contains(event.target) && setShowOption(false);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.addEventListener('mousedown', handleMouseDown, false);
    }
    return () => {
      if (typeof window !== 'undefined') {
        document.removeEventListener('mousedown', handleMouseDown, false);
      }
    };
  }, []);

  useEffect(() => {
    if (labelValue?.value !== sortByValue) {
      handleDropdownChange(sortByValue);
    }
  }, [sortByValue]);

  return (
    <StyledDropdown ref={dropdownRef} className="sort-dropdown-container">
      <div className="sort-label">
        <TextCustom {...dropdownTextStyle}>{sortLabel}</TextCustom>
      </div>

      <DropdownInput className={`dropdown-input ${showOption ? "open-content" : ""} `}>
        <StyledLabel
          onClick={toggleShowOption}
          disabled={disabled}
          className="dropdown-label"
        >
          <StyledLabelText className="dropdown-label-text sort-desktop">
            <TextCustom {...labelTextStyle}>{labelValue?.label}</TextCustom>
          </StyledLabelText>
          <div className="sort-mobile" tabIndex={0}>
            <SortArrow /> {sortShortLabel}
          </div>
          <StyledIcon className={`dropdown-icon  showOption && 'active'`}>
            <div className={`animation ${showOption && 'active'}`}>
              <ChevronDown variant="small"/>
            </div>
          </StyledIcon>
        </StyledLabel>
        {showOption && (
          <StyledDropdownBoxWrapper className="dropdown-options-content">
            <StyledDropdownBox className="dropdown-option">
              {sortFilterOptions?.map((child) => (
                <StyledDropdownBoxOption
                  className={`dropdown-option-wrapper ${child.value === sortByValue ? "active-label" : ""}` }
                  key={child.value || 'child-key'}
                  onClick={handleDropdownChange.bind(null, child.value)}
                >
                  {child.label}
                </StyledDropdownBoxOption>
              ))}
            </StyledDropdownBox>
          </StyledDropdownBoxWrapper>
        )}
      </DropdownInput>
    </StyledDropdown>
  );
};
